import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useTaskOptions, canonicalKeyForName, nameForCanonicalKey } from '../utils/taskOptions';
import { MultiSelectFilter } from '../components/MultiSelectFilter';
import { ActiveFilterChips } from '../components/ActiveFilterChips';
import { Palette, CheckCircle2, Clock, AlertCircle, FileText, Search, Filter, X, Calendar, User, Tag, Paintbrush, Layout, Figma, Layers, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { DatePicker } from '../components/DatePicker';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import TaskDetailModal from '../components/TaskDetailModal';
import { getBrandGradientClasses } from '../utils/brandColors';
import { sortTasksByTabStatus, getOrderingLabel } from '../utils/taskOrdering';
import { queueTaskEditHandoff } from '../utils/taskEditHandoff';
import { matchesTaskSearch } from '../utils/taskSearch';
import { useNavigate } from 'react-router-dom';
import { TaskIdBadge } from '../components/TaskIdBadge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TASKS_PER_PAGE_OPTIONS = [10, 25, 50];

// Emoji icon per UI/UX Status canonical_key — purely decorative, resolved by
// canonical_key so a rename of the display name doesn't lose its icon.
const DESIGN_STATUS_ICONS = {
  yet_to_start: '📋', in_research: '🔍', wireframing: '📐', designing: '🎨',
  in_review: '👀', revisions: '🔄', completed: '✅',
};
const DESIGN_STATUS_COLORS = {
  yet_to_start: 'bg-slate-100 text-slate-700 border-slate-200',
  in_research: 'bg-blue-50 text-blue-700 border-blue-200',
  wireframing: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  designing: 'bg-purple-50 text-purple-700 border-purple-200',
  in_review: 'bg-amber-50 text-amber-700 border-amber-200',
  revisions: 'bg-orange-50 text-orange-700 border-orange-200',
  completed: 'bg-green-50 text-green-700 border-green-200',
};

// Tab status mapping for horizontal tabs — a fixed 4-tab UI grouping over
// the (fully dynamic) UI/UX Statuses list; not itself a Task Option.
// "Pending Approval" mirrors the Developer Queue's 4-tab layout: it holds
// any task whose UI/UX completion is awaiting (or on standby for) a
// manager decision, regardless of what design_status it's parked at.
const TAB_STATUSES = ['Yet to Start', 'In Progress', 'Pending Approval', 'Completed'];

const UIUXDesignQueue = () => {
  const { token, user } = useAuth();
  const { CATEGORIES: DESIGN_CATEGORIES, UIUX_STATUSES, TASK_STATUSES, RAW: TASK_OPTIONS_RAW } = useTaskOptions();
  const dsKey = (name) => canonicalKeyForName(TASK_OPTIONS_RAW.uiux_statuses, name);
  const YET_TO_START_NAME = nameForCanonicalKey(TASK_OPTIONS_RAW.uiux_statuses, 'yet_to_start', UIUX_STATUSES[0] || 'Yet to Start');
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [taskNotes, setTaskNotes] = useState({});
  const [completionConfirm, setCompletionConfirm] = useState({ open: false, task: null, newStatus: null });
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUser, setFilterUser] = useState([]);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterOwnerStatus, setFilterOwnerStatus] = useState([]);
  const [filterDesignStatus, setFilterDesignStatus] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [designCompletedDate, setDesignCompletedDate] = useState('');
  
  // Active tab state
  const [activeTab, setActiveTab] = useState('Yet to Start');
  
  // Pagination states - separate for each tab
  const [paginationState, setPaginationState] = useState({
    'Yet to Start': { currentPage: 1, tasksPerPage: 10 },
    'In Progress': { currentPage: 1, tasksPerPage: 10 },
    'Pending Approval': { currentPage: 1, tasksPerPage: 10 },
    'Completed': { currentPage: 1, tasksPerPage: 10 }
  });
  
  // Use centralized permission from AuthContext
  const { isReadOnly } = useAuth();

  useEffect(() => {
    loadDesignTasks();
    loadUsers();
  }, []);

  const loadDesignTasks = async () => {
    try {
      // Load tasks with UI/UX dependency or UI/UX Design category
      const response = await axios.get(`${API}/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter for tasks with UI/UX dependency or UI/UX Design category
      const designTasks = response.data.filter(task => 
        task.uiux_dependency === true || task.category === 'UI/UX Design'
      );
      setTasks(designTasks);
      
      // Initialize notes state with existing notes from tasks
      const notesMap = {};
      designTasks.forEach(task => {
        notesMap[task.id] = task.notes || '';
      });
      setTaskNotes(notesMap);
    } catch (error) {
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const updateDesignStatus = async (taskId, newStatus) => {
    try {
      const updateData = { 
        design_status: newStatus,
        notes: taskNotes[taskId] || ''
      };
      
      // Set completion date if status resolves to the "Completed" canonical bucket.
      if (dsKey(newStatus) === 'completed') {
        updateData.design_end_date = new Date().toISOString();
      }
      
      const response = await axios.put(
        `${API}/tasks/${taskId}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.uiux_submitted_for_approval) {
        toast.success('UI/UX completion submitted for manager approval.');
      } else {
        toast.success('Design status updated!');
      }
      loadDesignTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Marking UI/UX Status as "Completed" sends it to Manager Approval instead
  // of completing it directly — confirm first so designers aren't surprised.
  // Managers are exempt server-side (they already hold approval authority),
  // so skip the confirm dialog for them too — it would complete immediately.
  const handleStatusSelect = (task, newStatus) => {
    if (dsKey(newStatus) === 'completed' && task.uiux_dependency && user?.role_level !== 'Manager') {
      setCompletionConfirm({ open: true, task, newStatus });
      return;
    }
    updateDesignStatus(task.id, newStatus);
  };

  const confirmCompletionSubmit = () => {
    if (completionConfirm.task) {
      updateDesignStatus(completionConfirm.task.id, completionConfirm.newStatus);
    }
    setCompletionConfirm({ open: false, task: null, newStatus: null });
  };

  const handleNotesChange = (taskId, value) => {
    setTaskNotes(prev => ({
      ...prev,
      [taskId]: value
    }));
  };

  const saveNotes = async (taskId) => {
    try {
      await axios.put(
        `${API}/tasks/${taskId}`,
        { notes: taskNotes[taskId] || '', note_source: 'uiux_note' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Notes saved!');
      loadDesignTasks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save notes');
    }
  };

  const getStatusIcon = (status) => {
    const key = dsKey(status);
    if (key === 'completed') return <CheckCircle2 className="text-green-600" size={20} />;
    if (key === 'designing' || key === 'wireframing') return <Paintbrush className="text-purple-600" size={20} />;
    if (key === 'in_research') return <Search className="text-blue-600" size={20} />;
    if (key === 'in_review') return <FileText className="text-amber-600" size={20} />;
    if (key === 'revisions') return <Clock className="text-orange-600" size={20} />;
    return <AlertCircle className="text-slate-500" size={20} />;
  };

  const getStatusColor = (status) => {
    return DESIGN_STATUS_COLORS[dsKey(status)] || 'bg-slate-50 text-slate-700 border-slate-200';
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user ? user.full_name : 'Unassigned';
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterUser([]);
    setFilterCategory('all');
    setFilterOwnerStatus([]);
    setFilterDesignStatus([]);
    setFromDate('');
    setToDate('');
    setDesignCompletedDate('');
    toast.success('All filters cleared');
  };

  const hasActiveFilters = searchTerm || filterUser.length > 0 || 
    (filterCategory && filterCategory !== 'all') || filterOwnerStatus.length > 0 || 
    filterDesignStatus.length > 0 ||
    fromDate || toDate || designCompletedDate;

  // Reset pagination when filters change
  useEffect(() => {
    setPaginationState(prev => ({
      'Yet to Start': { ...prev['Yet to Start'], currentPage: 1 },
      'In Progress': { ...prev['In Progress'], currentPage: 1 },
      'Pending Approval': { ...prev['Pending Approval'], currentPage: 1 },
      'Completed': { ...prev['Completed'], currentPage: 1 }
    }));
  }, [searchTerm, filterUser, filterCategory, filterOwnerStatus, filterDesignStatus, fromDate, toDate, designCompletedDate]);

  // Get current pagination state for active tab
  const currentPagination = paginationState[activeTab];
  const { currentPage, tasksPerPage } = currentPagination;

  // Handle page change
  const handlePageChange = (newPage) => {
    setPaginationState(prev => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], currentPage: newPage }
    }));
  };

  // Handle tasks per page change
  const handleTasksPerPageChange = (value) => {
    const newTasksPerPage = parseInt(value);
    setPaginationState(prev => ({
      ...prev,
      [activeTab]: { tasksPerPage: newTasksPerPage, currentPage: 1 }
    }));
  };

  // Handle tab change
  const handleTabChange = (status) => {
    setActiveTab(status);
    setPaginationState(prev => ({
      ...prev,
      [status]: { ...prev[status], currentPage: 1 }
    }));
  };

  // Apply all filters
  const filteredTasks = tasks.filter(task => {
    // Search filter
    if (searchTerm && !matchesTaskSearch(task, searchTerm)) {
      return false;
    }
    
    // User filter
    if (filterUser.length > 0 && !filterUser.includes(task.task_done_by)) {
      return false;
    }
    
    // Category filter (though all are UI/UX Design, keeping for consistency)
    if (filterCategory && filterCategory !== 'all' && task.category !== filterCategory) {
      return false;
    }
    
    // Owner status filter
    if (filterOwnerStatus.length > 0 && !filterOwnerStatus.includes(task.your_status)) {
      return false;
    }
    
    // Design Status filter
    if (filterDesignStatus.length > 0) {
      const taskDesignStatus = task.design_status || YET_TO_START_NAME;
      if (!filterDesignStatus.includes(taskDesignStatus)) {
        return false;
      }
    }
    
    // Date range filter
    if (fromDate || toDate) {
      const taskDate = task.start_date ? new Date(task.start_date) : null;
      if (!taskDate) return false;
      
      if (fromDate && taskDate < new Date(fromDate)) return false;
      if (toDate && taskDate > new Date(toDate)) return false;
    }
    
    // Design Completed Date filter
    if (designCompletedDate) {
      // Task must have a design_end_date to match this filter
      if (!task.design_end_date) return false;
      
      const completedDate = new Date(task.design_end_date);
      const filterDate = new Date(designCompletedDate);
      
      // Compare only the date portion (ignore time)
      const completedDateStr = completedDate.toISOString().split('T')[0];
      const filterDateStr = filterDate.toISOString().split('T')[0];
      
      if (completedDateStr !== filterDateStr) return false;
    }
    
    return true;
  });

  // Group tasks by tab status (4 tabs: Yet to Start, In Progress, Pending
  // Approval, Completed). Pending Approval holds any task whose UI/UX
  // completion is Pending or Standby — pulled out of its design_status
  // bucket (same model as the Developer Queue's Pending Approval tab).
  const DESIGN_COMPLETED_FIELDS = ['design_end_date', 'your_end_date', 'updated_at'];
  const isUiuxAwaitingApproval = (t) => ['Pending', 'Standby'].includes(t.uiux_approval_status);
  const groupedTasks = {
    'Yet to Start': sortTasksByTabStatus(
      filteredTasks.filter(t => !isUiuxAwaitingApproval(t) && (!t.design_status || dsKey(t.design_status) === 'yet_to_start')),
      'Yet to Start'
    ),
    'In Progress': sortTasksByTabStatus(
      filteredTasks.filter(t => {
        if (isUiuxAwaitingApproval(t)) return false;
        if (!t.design_status) return false;
        const key = dsKey(t.design_status);
        return key !== 'yet_to_start' && key !== 'completed';
      }),
      'In Progress'
    ),
    'Pending Approval': sortTasksByTabStatus(
      filteredTasks.filter(isUiuxAwaitingApproval),
      'Pending Approval'
    ),
    'Completed': sortTasksByTabStatus(
      filteredTasks.filter(t => !isUiuxAwaitingApproval(t) && dsKey(t.design_status) === 'completed'),
      'Completed',
      { completedDateFields: DESIGN_COMPLETED_FIELDS }
    ),
  };

  // Pagination calculations
  const activeTabTasks = groupedTasks[activeTab];
  const totalTasks = activeTabTasks.length;
  const totalPages = Math.ceil(totalTasks / tasksPerPage);
  const startIndex = (currentPage - 1) * tasksPerPage;
  const endIndex = startIndex + tasksPerPage;
  const paginatedTasks = activeTabTasks.slice(startIndex, endIndex);

  // Generate page numbers for pagination UI
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      const start = Math.max(2, currentPage - 1);
      const end = Math.min(totalPages - 1, currentPage + 1);
      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      if (!pages.includes(totalPages)) pages.push(totalPages);
    }
    return pages;
  };

  // Calculate stats for header
  const inProgressCount = filteredTasks.filter(t => {
    if (!t.design_status) return false;
    const key = dsKey(t.design_status);
    return key !== 'yet_to_start' && key !== 'completed';
  }).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="uiux-design-queue">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl text-white shadow-lg">
              <Palette size={32} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">UI/UX Design Queue</h1>
              <p className="text-slate-600 mt-1">Design tasks and workflows • {filteredTasks.length} total tasks</p>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="text-center px-4 py-2 bg-white rounded-lg border border-slate-200 shadow-sm">
              <p className="text-xs text-slate-500 uppercase tracking-wide">Yet to Start</p>
              <p className="text-2xl font-bold text-slate-600">{groupedTasks['Yet to Start'].length}</p>
            </div>
            <div className="text-center px-4 py-2 bg-white rounded-lg border border-purple-200 shadow-sm">
              <p className="text-xs text-purple-600 uppercase tracking-wide">In Progress</p>
              <p className="text-2xl font-bold text-purple-600">{groupedTasks['In Progress'].length}</p>
            </div>
            <div className="text-center px-4 py-2 bg-white rounded-lg border border-amber-200 shadow-sm" data-testid="stat-pending-approval">
              <p className="text-xs text-amber-600 uppercase tracking-wide">Pending Approval</p>
              <p className="text-2xl font-bold text-amber-600">{groupedTasks['Pending Approval'].length}</p>
            </div>
            <div className="text-center px-4 py-2 bg-white rounded-lg border border-green-200 shadow-sm">
              <p className="text-xs text-green-600 uppercase tracking-wide">Completed</p>
              <p className="text-2xl font-bold text-green-600">{groupedTasks['Completed'].length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-3 mb-4">
          <Filter className="text-purple-600" size={20} />
          <h2 className="text-lg font-semibold text-slate-900">Filters</h2>
          {hasActiveFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearAllFilters}
              className="ml-auto text-slate-600 border-slate-300 hover:bg-slate-50"
              data-testid="clear-all-filters"
            >
              <X size={14} className="mr-1" />
              Clear All Filters
            </Button>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Search */}
          <div className="lg:col-span-2">
            <Label className="text-xs text-slate-600 mb-2 flex items-center gap-1">
              <Search size={12} />
              Search Tasks
            </Label>
            <Input
              type="text"
              placeholder="Search by task name or Task ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
              data-testid="search-tasks"
            />
          </div>

          {/* User Filter */}
          <div>
            <Label className="text-xs text-slate-600 mb-2 flex items-center gap-1">
              <User size={12} />
              Assigned Designer
            </Label>
            <MultiSelectFilter
              label="Assigned Designer"
              placeholder="All Designers"
              options={users.map(u => ({ value: u.id, label: u.full_name, searchText: u.email }))}
              selectedValues={filterUser}
              onChange={setFilterUser}
              testId="filter-user"
            />
          </div>

          {/* Owner Status Filter */}
          <div>
            <Label className="text-xs text-slate-600 mb-2">Owner Status</Label>
            <MultiSelectFilter
              label="Owner Status"
              placeholder="All Statuses"
              options={TASK_STATUSES}
              selectedValues={filterOwnerStatus}
              onChange={setFilterOwnerStatus}
              testId="filter-owner-status"
            />
          </div>

          {/* Design Status Filter */}
          <div>
            <Label className="text-xs text-slate-600 mb-2 flex items-center gap-1">
              <Paintbrush size={12} />
              Design Status
            </Label>
            <MultiSelectFilter
              label="Design Status"
              placeholder="All Statuses"
              options={UIUX_STATUSES.map(status => ({ value: status, label: `${DESIGN_STATUS_ICONS[dsKey(status)] || ''} ${status}` }))}
              selectedValues={filterDesignStatus}
              onChange={setFilterDesignStatus}
              testId="filter-design-status"
            />
          </div>

          {/* Date Range */}
          <div className="lg:col-span-2 grid grid-cols-3 gap-2">
            <div>
              <Label className="text-xs text-slate-600 mb-2 flex items-center gap-1">
                <Calendar size={12} />
                From Date
              </Label>
              <DatePicker
                value={fromDate}
                onChange={setFromDate}
                testId="filter-from-date"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-2">To Date</Label>
              <DatePicker
                value={toDate}
                onChange={setToDate}
                testId="filter-to-date"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-600 mb-2 flex items-center gap-1">
                <CheckCircle2 size={12} />
                Design Completed
              </Label>
              <DatePicker
                value={designCompletedDate}
                onChange={setDesignCompletedDate}
                testId="filter-design-completed-date"
              />
            </div>
          </div>
        </div>

        {/* Active Filters Indicator */}
        {hasActiveFilters && (
          <div className="mt-4 pt-4 border-t border-slate-200">
            <p className="text-sm text-slate-600">
              <span className="font-semibold">Showing:</span> {filteredTasks.length} of {tasks.length} design tasks
            </p>
            <ActiveFilterChips
              chips={[
                filterUser.length > 0 && { key: 'user', label: `${filterUser.length} Designer${filterUser.length > 1 ? 's' : ''}`, onRemove: () => setFilterUser([]) },
                filterOwnerStatus.length > 0 && { key: 'owner-status', label: `${filterOwnerStatus.length} Status${filterOwnerStatus.length > 1 ? 'es' : ''}`, onRemove: () => setFilterOwnerStatus([]) },
                filterDesignStatus.length > 0 && { key: 'design-status', label: `${filterDesignStatus.length} Design Status${filterDesignStatus.length > 1 ? 'es' : ''}`, onRemove: () => setFilterDesignStatus([]) },
              ].filter(Boolean)}
              onClearAll={clearAllFilters}
            />
          </div>
        )}
      </div>

      {/* Design Status Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center gap-2" data-testid="design-status-tabs">
          {TAB_STATUSES.map((status) => {
            const count = groupedTasks[status].length;
            const isActive = activeTab === status;
            
            return (
              <button
                key={status}
                onClick={() => handleTabChange(status)}
                data-testid={`design-tab-${status.toLowerCase().replace(/\s+/g, '-')}`}
                className={`
                  flex items-center gap-3 px-5 py-3 rounded-full font-semibold transition-all duration-200
                  ${isActive 
                    ? 'bg-purple-600 text-white shadow-lg' 
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }
                `}
              >
                <span className="text-sm">{status}</span>
                <span className={`text-lg font-bold ${isActive ? 'text-white' : 'text-slate-800'}`}>
                  {count.toString().padStart(2, '0')}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Task List for Active Tab */}
      <div className="space-y-4">
        <div className="flex items-center justify-between pb-2 border-b-2 border-slate-200">
          <div className="flex items-center gap-3">
            {activeTab === 'Pending Approval' ? <AlertCircle className="text-amber-600" size={20} /> : getStatusIcon(activeTab === 'Yet to Start' ? YET_TO_START_NAME : activeTab === 'Completed' ? nameForCanonicalKey(TASK_OPTIONS_RAW.uiux_statuses, 'completed', 'Completed') : nameForCanonicalKey(TASK_OPTIONS_RAW.uiux_statuses, 'designing', 'Designing'))}
            <h2 className="text-2xl font-bold text-slate-800">{activeTab}</h2>
            <span className="px-3 py-1 rounded-full text-sm font-semibold bg-slate-100 text-slate-700">
              {totalTasks} {totalTasks === 1 ? 'task' : 'tasks'}
            </span>
            <span
              data-testid="design-queue-sort-indicator"
              className="hidden sm:inline-flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-purple-50 text-purple-700 border border-purple-200 font-medium"
              title="Default ordering applied to this tab"
            >
              Sorted: {getOrderingLabel(activeTab)}
            </span>
          </div>
          {totalTasks > 0 && (
            <span className="text-sm text-slate-500">
              Showing {startIndex + 1}-{Math.min(endIndex, totalTasks)} of {totalTasks}
            </span>
          )}
        </div>
        
        {totalTasks === 0 ? (
          <div className="bg-gradient-to-br from-slate-50 to-purple-50 rounded-xl border-2 border-dashed border-slate-300 p-12 text-center">
            <Layers className="text-slate-400 mx-auto mb-3" size={48} />
            <p className="text-slate-500 text-lg font-medium">No {activeTab.toLowerCase()} tasks</p>
            <p className="text-slate-400 text-sm mt-1">
              {hasActiveFilters ? 'Try adjusting your filters' : 'All tasks are in other stages'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {paginatedTasks.map((task) => (
              <div
                key={task.id}
                data-testid={`design-task-${task.id}`}
                onClick={() => {
                  setSelectedTask(task);
                  setIsDetailOpen(true);
                }}
                className="group bg-white rounded-lg shadow-sm border border-slate-200 hover:border-purple-400 p-3 hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  {/* Left Content - Task Details */}
                  <div className="flex-1 min-w-0">
                    {/* Top Row: Badges */}
                    <div className="flex items-center gap-1.5 mb-1.5 flex-wrap">
                      <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-semibold bg-purple-100 text-purple-700">
                        <Palette size={10} />
                        {task.category}
                      </span>
                      {task.brand && (
                        <span className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-semibold ${getBrandGradientClasses(task.brand)}`}>
                          {task.brand}
                        </span>
                      )}
                      {task.your_status && (
                        <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${
                          task.your_status === 'Completed' ? 'bg-green-100 text-green-700' :
                          task.your_status === 'In Process' ? 'bg-blue-100 text-blue-700' :
                          task.your_status === 'On Hold' ? 'bg-red-100 text-red-700' :
                          task.your_status === 'Backlog' ? 'bg-slate-100 text-slate-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {task.your_status}
                        </span>
                      )}
                      {task.task_done_by && (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-semibold bg-slate-100 text-slate-600">
                          <User size={10} />
                          {getUserName(task.task_done_by)}
                        </span>
                      )}
                      {task.design_status && dsKey(task.design_status) !== 'yet_to_start' && (
                        <span className={`text-[10px] px-2 py-0.5 rounded font-semibold ${getStatusColor(task.design_status)}`}>
                          {DESIGN_STATUS_ICONS[dsKey(task.design_status)] || ''} {task.design_status}
                        </span>
                      )}
                      {task.uiux_approval_status === 'Pending' && (
                        <span
                          data-testid={`uiux-pending-badge-${task.id}`}
                          className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-semibold bg-amber-100 text-amber-700 border border-amber-200"
                          title="UI/UX completion awaiting manager approval"
                        >
                          <Clock size={10} />
                          Pending Manager Approval
                        </span>
                      )}
                      {task.uiux_approval_status === 'Standby' && (
                        <span
                          data-testid={`uiux-standby-badge-${task.id}`}
                          className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded font-semibold bg-orange-100 text-orange-700 border border-orange-200"
                          title="UI/UX completion on standby"
                        >
                          <Clock size={10} />
                          On Standby
                        </span>
                      )}
                    </div>
                    
                    {/* Task Title */}
                    <h3 className="text-sm font-semibold text-slate-900 mb-1.5 group-hover:text-purple-600 transition-colors line-clamp-2">
                      <TaskIdBadge taskId={task.task_id} className="mr-1.5 align-middle" />
                      {task.task}
                    </h3>
                    
                    {/* Task Output - Compact inline */}
                    {task.output && (
                      <div className="flex items-start gap-2 mb-1.5">
                        <span className="text-[10px] font-semibold text-purple-600 uppercase whitespace-nowrap">Deliverable:</span>
                        <p className="text-xs text-slate-600 line-clamp-1">{task.output}</p>
                      </div>
                    )}
                    
                    {/* Dates - More compact */}
                    <div className="flex items-center gap-3 text-[10px] text-slate-400">
                      {task.start_date && (
                        <span className="flex items-center gap-1">
                          <Calendar size={10} />
                          {format(new Date(task.start_date), 'MMM dd, yyyy')}
                        </span>
                      )}
                      {task.design_end_date && (
                        <span className="flex items-center gap-1 text-green-600 font-semibold">
                          <CheckCircle2 size={10} />
                          Done: {format(new Date(task.design_end_date), 'MMM dd')}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Right Content - Actions (Compact Horizontal) */}
                  {!isReadOnly && (
                    <div className="flex items-start gap-2 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                      {/* Status Selector */}
                      <div className="bg-purple-50 rounded p-1.5 border border-purple-200 min-w-[130px]">
                        <Label className="text-[9px] font-semibold text-purple-600 mb-0.5 uppercase block">
                          Status
                        </Label>
                        {['Pending', 'Standby'].includes(task.uiux_approval_status) ? (
                          <div
                            data-testid={`uiux-pending-approval-${task.id}`}
                            className={`h-7 px-2 flex items-center justify-center text-[11px] font-semibold rounded border ${
                              task.uiux_approval_status === 'Standby'
                                ? 'bg-orange-100 text-orange-700 border-orange-200'
                                : 'bg-amber-100 text-amber-700 border-amber-200'
                            }`}
                            title={task.uiux_approval_status === 'Standby' ? 'On standby with manager' : 'Awaiting manager approval'}
                          >
                            {task.uiux_approval_status === 'Standby' ? 'On Standby' : 'Pending Approval'}
                          </div>
                        ) : (
                          <Select
                            value={task.design_status || YET_TO_START_NAME}
                            onValueChange={(value) => handleStatusSelect(task, value)}
                          >
                            <SelectTrigger className="bg-white border-purple-200 h-7 text-[11px]" data-testid={`design-status-select-${task.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {UIUX_STATUSES.map(status => (
                                <SelectItem key={status} value={status}>
                                  {DESIGN_STATUS_ICONS[dsKey(status)] || ''} {status}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        {task.uiux_approval_status === 'Rejected' && task.uiux_manager_notes && (
                          <div
                            data-testid={`uiux-rejection-notes-${task.id}`}
                            className="mt-1 text-[10px] text-red-700 bg-red-50 border border-red-100 rounded px-1.5 py-1 leading-tight"
                            title={task.uiux_manager_notes}
                          >
                            <span className="font-semibold">Rejected:</span> {task.uiux_manager_notes}
                          </div>
                        )}
                      </div>
                      
                      {/* Designer Notes - Compact Inline */}
                      <div className="bg-slate-50 rounded p-1.5 border border-slate-200 min-w-[180px]">
                        <Label className="text-[9px] font-semibold text-slate-500 mb-0.5 uppercase flex items-center gap-0.5">
                          <FileText size={9} />
                          Notes
                        </Label>
                        <div className="flex gap-1">
                          <Input
                            value={taskNotes[task.id] || ''}
                            onChange={(e) => handleNotesChange(task.id, e.target.value)}
                            placeholder="Add notes..."
                            className="h-7 text-[11px] flex-1"
                            data-testid={`design-notes-${task.id}`}
                          />
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              saveNotes(task.id);
                            }}
                            className="h-7 px-2 text-[10px] bg-purple-500 hover:bg-purple-600"
                            data-testid={`save-notes-${task.id}`}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination Controls */}
        {totalTasks > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 mt-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              {/* Page Navigation */}
              <div className="flex items-center gap-2" data-testid="pagination-controls">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex items-center gap-1"
                  data-testid="pagination-prev"
                >
                  <ChevronLeft size={16} />
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {getPageNumbers().map((page, index) => (
                    page === '...' ? (
                      <span key={`ellipsis-${index}`} className="px-2 text-slate-400">...</span>
                    ) : (
                      <Button
                        key={page}
                        variant={currentPage === page ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(page)}
                        className={`min-w-[40px] ${currentPage === page ? 'bg-purple-600 text-white' : ''}`}
                        data-testid={`pagination-page-${page}`}
                      >
                        {page}
                      </Button>
                    )
                  ))}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex items-center gap-1"
                  data-testid="pagination-next"
                >
                  Next
                  <ChevronRight size={16} />
                </Button>
              </div>
              
              {/* Tasks Per Page Selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-600">Tasks per page:</span>
                <Select value={tasksPerPage.toString()} onValueChange={handleTasksPerPageChange}>
                  <SelectTrigger className="w-[80px]" data-testid="tasks-per-page-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TASKS_PER_PAGE_OPTIONS.map(option => (
                      <SelectItem key={option} value={option.toString()}>{option}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Page Info */}
            <div className="mt-3 pt-3 border-t border-slate-200 text-center">
              <span className="text-sm text-slate-500">
                Page {currentPage} of {totalPages} • Showing {startIndex + 1}-{Math.min(endIndex, totalTasks)} of {totalTasks} tasks
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Empty State when no UI/UX Design tasks exist */}
      {tasks.length === 0 && (
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border-2 border-dashed border-purple-300 p-16 text-center">
          <div className="p-4 bg-white rounded-full w-20 h-20 mx-auto mb-4 shadow-lg flex items-center justify-center">
            <Palette className="text-purple-500" size={40} />
          </div>
          <h3 className="text-2xl font-bold text-slate-900 mb-2">No UI/UX Design Tasks</h3>
          <p className="text-slate-600 max-w-md mx-auto">
            There are currently no tasks with UI/UX dependency or &quot;UI/UX Design&quot; category. 
            Create a new task with the UI/UX Dependency checkbox checked to see it here.
          </p>
        </div>
      )}

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTask}
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        users={users}
        onTaskUpdated={loadDesignTasks}
        onEdit={(task) => {
          queueTaskEditHandoff(task.id);
          setIsDetailOpen(false);
          navigate('/tasks');
        }}
      />

      {/* Completion Submission Confirm Dialog */}
      <AlertDialog
        open={completionConfirm.open}
        onOpenChange={(open) => !open && setCompletionConfirm({ open: false, task: null, newStatus: null })}
      >
        <AlertDialogContent data-testid="uiux-completion-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Submit for Manager Approval?</AlertDialogTitle>
            <AlertDialogDescription>
              Marking this task's UI/UX Status as Completed will submit it to your manager for approval.
              The UI/UX Status won't be marked Completed until the manager approves it. You can keep
              working on the task while it's pending.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="uiux-completion-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmCompletionSubmit}
              data-testid="uiux-completion-confirm-submit"
            >
              Submit for Approval
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UIUXDesignQueue;
